import { appendQualitySlide } from './reportQualityExport';
import pptxgen from 'pptxgenjs';

export const generateCyberSecurityPptx = (data) => {
  const pptx = new pptxgen();
  pptx.author = 'SAMANVAI';
  pptx.title = `Cyber Security Assessment - ${data.company_name}`;
  pptx.subject = 'Cybersecurity Maturity Report';
  pptx.company = 'SAMANVAI';

  const C = {
    primary: '059669', secondary: '10B981', accent: '0EA5E9',
    dark: '0F172A', darkGray: '1F2937', medGray: '4B5563', lightGray: 'F3F4F6',
    white: 'FFFFFF', red: 'EF4444', orange: 'F97316', amber: 'EAB308',
    green: '22C55E', blue: '2563EB', purple: '7C3AED', teal: '0891B2',
  };

  const domainColors = {
    'Governance & Risk': C.blue, 'Identity & Access Management': C.primary,
    'Network & Infrastructure': C.red, 'Endpoint & Detection': C.orange,
    'Incident Response': C.purple, 'Awareness & Culture': C.teal,
  };

  const scoreColor = (s) => {
    if (s >= 4.5) return C.accent;
    if (s >= 3.5) return C.green;
    if (s >= 2.5) return C.amber;
    if (s >= 1.5) return C.orange;
    return C.red;
  };

  const maturityLabel = (s) => {
    const r = Math.round(s);
    return { 1: 'Initial', 2: 'Developing', 3: 'Defined', 4: 'Managed', 5: 'Optimised' }[r] || 'N/A';
  };

  const domains = data.domain_scores || {};
  const scores = data.scores || {};
  const overall = data.overall_score || 0;

  // ── Slide 1: Title ──
  const s1 = pptx.addSlide();
  s1.background = { color: C.dark };
  s1.addText('SAMANVAI', { x: 0.5, y: 0.4, w: 3, h: 0.5, fontSize: 24, bold: true, color: C.primary });
  s1.addText('Agentic AI for CXOs', { x: 0.5, y: 0.85, w: 3, h: 0.3, fontSize: 10, color: C.accent });
  s1.addText('Cyber Security', { x: 0.5, y: 2, w: 7, h: 0.9, fontSize: 44, bold: true, color: C.white });
  s1.addText('Maturity Assessment', { x: 0.5, y: 2.85, w: 7, h: 0.7, fontSize: 36, color: C.secondary });
  s1.addText(data.company_name || '', { x: 0.5, y: 4.0, w: 7, h: 0.5, fontSize: 20, color: C.accent });
  const meta = [data.industry, data.org_size].filter(Boolean).join('  |  ');
  s1.addText(meta, { x: 0.5, y: 4.5, w: 7, h: 0.4, fontSize: 12, color: C.medGray });
  s1.addText(`Prepared: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, { x: 0.5, y: 5, w: 4, h: 0.3, fontSize: 10, color: '6B7280' });
  s1.addText('Aligned with NIST CSF, ISO 27001, CIS Controls', { x: 5, y: 5, w: 4.5, h: 0.3, fontSize: 9, color: '6B7280', align: 'right' });

  // ── Slide 2: Executive Summary ──
  const s2 = pptx.addSlide();
  s2.background = { color: C.white };
  s2.addText('Executive Summary', { x: 0.5, y: 0.3, w: 8, h: 0.6, fontSize: 28, bold: true, color: C.dark });
  s2.addShape(pptx.ShapeType.rect, { x: 0.5, y: 0.85, w: 2, h: 0.04, fill: { color: C.primary } });

  // Score box
  s2.addShape(pptx.ShapeType.rect, { x: 0.5, y: 1.3, w: 3, h: 1.8, fill: { color: C.dark }, rectRadius: 0.15 });
  s2.addText(`${overall}`, { x: 0.5, y: 1.5, w: 3, h: 0.8, fontSize: 48, bold: true, color: scoreColor(overall), align: 'center' });
  s2.addText('/ 5.0', { x: 0.5, y: 2.15, w: 3, h: 0.3, fontSize: 14, color: '9CA3AF', align: 'center' });
  s2.addText(maturityLabel(overall), { x: 0.5, y: 2.55, w: 3, h: 0.35, fontSize: 16, bold: true, color: scoreColor(overall), align: 'center' });

  // Key stats
  const domainEntries = Object.entries(domains);
  const strongest = domainEntries.reduce((a, b) => a[1].score > b[1].score ? a : b, domainEntries[0] || ['', { name: '', score: 0 }]);
  const weakest = domainEntries.reduce((a, b) => a[1].score < b[1].score ? a : b, domainEntries[0] || ['', { name: '', score: 0 }]);
  const critGaps = Object.values(scores).filter(v => v <= 1).length;
  const highGaps = Object.values(scores).filter(v => v === 2).length;

  const stats = [
    { label: 'Strongest Domain', value: `${strongest[1].name} (${strongest[1].score})`, color: C.green },
    { label: 'Weakest Domain', value: `${weakest[1].name} (${weakest[1].score})`, color: C.red },
    { label: 'Critical Gaps', value: `${critGaps} controls`, color: C.red },
    { label: 'High-Risk Gaps', value: `${highGaps} controls`, color: C.orange },
  ];
  stats.forEach((st, i) => {
    const y = 1.3 + i * 0.44;
    s2.addText(st.label, { x: 4, y, w: 2.5, h: 0.35, fontSize: 10, color: C.medGray });
    s2.addText(st.value, { x: 6.5, y, w: 3, h: 0.35, fontSize: 11, bold: true, color: st.color, align: 'right' });
  });

  // Summary text
  const summaryText = overall < 2
    ? `${data.company_name} is at an early stage of cybersecurity maturity with critical gaps that require urgent remediation. A board-sponsored programme is recommended.`
    : overall < 3
    ? `${data.company_name} has developing cybersecurity capabilities but faces notable gaps. A structured 90-day improvement programme is recommended.`
    : overall < 4
    ? `${data.company_name} has a solid cybersecurity foundation with room for optimisation. Focus on automation and proactive threat management.`
    : `${data.company_name} demonstrates strong cybersecurity maturity. Continued investment in advanced detection and threat intelligence is recommended.`;
  s2.addText(summaryText, { x: 0.5, y: 3.5, w: 9, h: 1.2, fontSize: 12, color: C.medGray, lineSpacing: 20 });

  // ── Slide 3: Domain Scores ──
  const s3 = pptx.addSlide();
  s3.background = { color: C.white };
  s3.addText('Domain Scores', { x: 0.5, y: 0.3, w: 8, h: 0.6, fontSize: 28, bold: true, color: C.dark });
  s3.addShape(pptx.ShapeType.rect, { x: 0.5, y: 0.85, w: 2, h: 0.04, fill: { color: C.primary } });

  domainEntries.forEach(([did, ddata], i) => {
    const y = 1.2 + i * 0.7;
    const col = domainColors[ddata.name] || C.primary;
    s3.addText(ddata.name, { x: 0.5, y, w: 4, h: 0.35, fontSize: 13, bold: true, color: C.dark });
    s3.addText(maturityLabel(ddata.score), { x: 0.5, y: y + 0.3, w: 4, h: 0.25, fontSize: 9, color: C.medGray });

    // Score bar background
    s3.addShape(pptx.ShapeType.rect, { x: 4.8, y: y + 0.05, w: 4, h: 0.25, fill: { color: C.lightGray }, rectRadius: 0.05 });
    // Score bar fill
    const barW = Math.max((ddata.score / 5) * 4, 0.1);
    s3.addShape(pptx.ShapeType.rect, { x: 4.8, y: y + 0.05, w: barW, h: 0.25, fill: { color: col }, rectRadius: 0.05 });
    s3.addText(`${ddata.score}/5`, { x: 9, y, w: 0.8, h: 0.35, fontSize: 13, bold: true, color: col, align: 'right' });
  });

  // ── Slide 4: Key Findings ──
  const findings = data.findings || [];
  if (findings.length > 0) {
    const s4 = pptx.addSlide();
    s4.background = { color: C.white };
    s4.addText('Key Findings', { x: 0.5, y: 0.3, w: 8, h: 0.6, fontSize: 28, bold: true, color: C.dark });
    s4.addShape(pptx.ShapeType.rect, { x: 0.5, y: 0.85, w: 2, h: 0.04, fill: { color: C.primary } });

    const critCount = findings.filter(f => f.priority === 'Critical').length;
    const highCount = findings.filter(f => f.priority === 'High').length;
    const medCount = findings.filter(f => f.priority === 'Medium').length;
    s4.addText(`${critCount} Critical  |  ${highCount} High  |  ${medCount} Medium`, {
      x: 0.5, y: 1.0, w: 9, h: 0.35, fontSize: 12, color: C.medGray
    });

    const topFindings = findings.slice(0, 8);
    topFindings.forEach((f, i) => {
      const y = 1.5 + i * 0.48;
      const pColor = f.priority === 'Critical' ? C.red : f.priority === 'High' ? C.orange : C.amber;
      s4.addShape(pptx.ShapeType.rect, { x: 0.5, y: y + 0.05, w: 0.08, h: 0.3, fill: { color: pColor } });
      s4.addText(f.control, { x: 0.75, y, w: 6.5, h: 0.35, fontSize: 10, color: C.dark });
      s4.addText(`L${f.current_level} -> L${f.target_level}`, { x: 7.5, y, w: 1, h: 0.35, fontSize: 9, color: C.medGray, align: 'center' });
      s4.addText(f.priority, { x: 8.6, y, w: 1.2, h: 0.35, fontSize: 9, bold: true, color: pColor, align: 'right' });
    });
  }

  // ── Slide 5: Recommendations ──
  const s5 = pptx.addSlide();
  s5.background = { color: C.white };
  s5.addText('Recommendations Roadmap', { x: 0.5, y: 0.3, w: 8, h: 0.6, fontSize: 28, bold: true, color: C.dark });
  s5.addShape(pptx.ShapeType.rect, { x: 0.5, y: 0.85, w: 2, h: 0.04, fill: { color: C.primary } });

  const timeframes = [
    { label: 'Immediate', desc: 'Within 1 week', color: C.red, x: 0.3 },
    { label: '30 Days', desc: 'Short-term fixes', color: C.orange, x: 2.7 },
    { label: '90 Days', desc: 'Medium-term improvements', color: C.amber, x: 5.1 },
    { label: 'Ongoing', desc: 'Continuous improvement', color: C.green, x: 7.5 },
  ];

  timeframes.forEach((tf) => {
    s5.addShape(pptx.ShapeType.rect, { x: tf.x, y: 1.2, w: 2.2, h: 3.6, fill: { color: C.lightGray }, rectRadius: 0.1 });
    s5.addShape(pptx.ShapeType.rect, { x: tf.x, y: 1.2, w: 2.2, h: 0.06, fill: { color: tf.color }, rectRadius: 0.05 });
    s5.addText(tf.label, { x: tf.x + 0.1, y: 1.35, w: 2, h: 0.35, fontSize: 14, bold: true, color: C.dark });
    s5.addText(tf.desc, { x: tf.x + 0.1, y: 1.65, w: 2, h: 0.25, fontSize: 8, color: C.medGray });
  });

  // Add recommendation items
  const recMap = {
    immediate: [], '30day': [], '90day': [], ongoing: [],
  };
  const RECS = {
    gov_1: { immediate: 'Draft/update security policy', '30day': 'Board approval & distribution', '90day': 'Annual review cycle', ongoing: 'Regulation alignment' },
    iam_1: { immediate: 'Enable MFA for admins', '30day': 'MFA for all remote users', '90day': 'Org-wide MFA', ongoing: 'Phishing-resistant MFA' },
    net_1: { immediate: 'Map network architecture', '30day': 'VLAN segmentation', '90day': 'Micro-segmentation', ongoing: 'Zero-trust progression' },
    edr_1: { immediate: 'Deploy EDR agents', '30day': 'Tune detection policies', '90day': '100% endpoint coverage', ongoing: 'Threat hunting' },
    ir_1: { immediate: 'Update IR plan', '30day': 'Tabletop exercise', '90day': 'Full simulation', ongoing: 'Bi-annual exercises' },
    aw_1: { immediate: 'Schedule security training', '30day': 'Interactive programme', '90day': '95% completion rate', ongoing: 'Annual refresher' },
  };

  Object.entries(scores).forEach(([cid, score]) => {
    const rec = RECS[cid];
    if (!rec) return;
    if (score <= 2) {
      recMap.immediate.push(rec.immediate);
      recMap['30day'].push(rec['30day']);
    }
    if (score <= 3) recMap['90day'].push(rec['90day']);
    recMap.ongoing.push(rec.ongoing);
  });

  const addRecs = (items, xPos, maxItems = 5) => {
    items.slice(0, maxItems).forEach((item, i) => {
      const y = 2.0 + i * 0.38;
      s5.addText(`- ${item}`, { x: xPos + 0.1, y, w: 2, h: 0.32, fontSize: 8, color: C.medGray });
    });
  };
  addRecs(recMap.immediate, 0.3);
  addRecs(recMap['30day'], 2.7);
  addRecs(recMap['90day'], 5.1);
  addRecs(recMap.ongoing, 7.5);

  // ── Slide 6: Thank You ──
  const s6 = pptx.addSlide();
  s6.background = { color: C.dark };
  s6.addText('Thank You', { x: 0.5, y: 1.8, w: 9, h: 0.9, fontSize: 44, bold: true, color: C.white, align: 'center' });
  s6.addText('For a deeper dive into your results, connect with us.', { x: 1, y: 2.8, w: 8, h: 0.5, fontSize: 16, color: C.secondary, align: 'center' });
  s6.addText('karna.vijay@gmail.com  |  SAMANVAI', { x: 1, y: 3.6, w: 8, h: 0.4, fontSize: 12, color: '9CA3AF', align: 'center' });
  s6.addText('Powered by SAMANVAI  |  Confidential', { x: 0.5, y: 4.8, w: 9, h: 0.3, fontSize: 9, color: '4B5563', align: 'center' });

  appendQualitySlide(pptx, data);
  return pptx.writeFile({ fileName: `CyberSecurity_Assessment_${(data.company_name || 'Report').replace(/\s+/g, '_')}.pptx` });
};
